import { motion } from "framer-motion";
import {
  Check,
  ArrowLeft,
  MessageCircle,
  Users,
  User,
  Briefcase,
  Plane,
  Send,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

const WHATSAPP_NUMBER = "19548059212";

const buildWhatsAppUrl = (planLabel) => {
  const message = encodeURIComponent(
    `Hola, soy agente de Divídelo. Acabo de llevar a mi cliente a la página /preciosagentes para el plan: ${planLabel}.\n\nEstoy haciendo el seguimiento de esta venta.`
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
};

const PLANS = [
  {
    id: "profesional",
    icon: Briefcase,
    name: "Profesional",
    tagline: "Para agentes con proceso profesional y clientes recurrentes",
    familyLabel: "Comisión sobre valor procesado",
    familyPrice: "60/40",
    familyPriceUnit: "agente / plataforma",
    badge: "PROFESIONAL",
    color: "blue",
    gradient: "from-blue-500 to-indigo-600",
    borderClass: "border-blue-300",
    priceColor: "text-blue-600",
    freedomTitle: "60% del valor procesado se lo lleva el agente",
    freedomBody:
      "El 40% restante lo retiene Divídelo para cubrir procesamiento, plataforma y soporte VIP al cliente. Tú fijas el precio y cobras al cliente; nosotros liquidamos tu 60% por cada venta cerrada.",
    features: [
      "Panel exclusivo de comisiones",
      "Reporte de ventas mensual",
      "Prioridad en soporte al agente",
      "Materiales de venta descargables",
      "Acompañamiento del proceso migratorio",
    ],
  },
  {
    id: "travel-empire",
    icon: Plane,
    name: "Travel Empire",
    tagline: "Programa insignia para familias que quieren viajar juntas",
    familyLabel: "Gratis para atención familiar",
    familyPrice: "$0.00",
    familyPriceUnit: "USD",
    badge: "TRAVEL EMPIRE · GRATIS FAMILIA",
    color: "amber",
    gradient: "from-amber-500 to-orange-600",
    borderClass: "border-amber-300",
    priceColor: "text-amber-600",
    freedomTitle: "Travel Empire es GRATIS si son familia",
    freedomBody:
      "Nuestro programa insignia para grupos familiares. Cero costo si viajan juntos como núcleo familiar. Es nuestra apuesta para que la familia entera confíe en Divídelo desde el primer viaje.",
    features: [
      "Adelanto de cita consular incluido",
      "Bonos y beneficios de viaje para familia",
      "Coordinación consular grupal",
      "Acompañamiento por asesor Travel Empire",
      "Alertas y seguimiento continuo",
    ],
  },
];

const IndividualNotice = () => (
  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
    <User className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-semibold text-amber-800">
        Clientes individuales: $24.99 USD
      </p>
      <p className="text-xs text-amber-700 mt-1">
        Si el cliente es una persona sola (no familia), su tarifa siempre es de{" "}
        <strong>$24.99 USD por persona</strong>, sin importar el plan. Coordina
        el pago por WhatsApp.
      </p>
    </div>
  </div>
);

const FamilyNotice = ({ familyLabel }) => (
  <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
    <Users className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-semibold text-emerald-800">
        Modalidad familiar
      </p>
      <p className="text-xs text-emerald-700 mt-1">
        {familyLabel}. La atención en modalidad familiar aplica cuando el grupo
        es un núcleo familiar directo.
      </p>
    </div>
  </div>
);

const PlanCard = ({ plan }) => {
  const Icon = plan.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative bg-white rounded-2xl shadow-xl border-2 ${plan.borderClass} overflow-hidden flex flex-col`}
      data-testid={`agent-plan-card-${plan.id}`}
    >
      <div
        className={`absolute top-0 left-0 right-0 text-center py-2 bg-gradient-to-r ${plan.gradient} text-white text-xs font-bold uppercase tracking-widest`}
      >
        {plan.badge}
      </div>

      <div className="p-8 pt-16 flex-1 flex flex-col">
        <div
          className={`w-14 h-14 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center mb-5`}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        <h2 className="font-heading text-2xl font-bold text-navy-900 mb-1">
          {plan.name}
        </h2>
        <p className="text-navy-500 text-sm mb-6">{plan.tagline}</p>

        {/* Price / commission block */}
        <div className="mb-6">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-5xl font-bold ${plan.priceColor}`}>
              {plan.familyPrice}
            </span>
            <span className="text-navy-500 text-sm">{plan.familyPriceUnit}</span>
          </div>
          <span className="text-navy-500 text-sm">{plan.familyLabel}</span>
        </div>

        {/* Freedom / commission highlight */}
        <div
          className={`mb-6 rounded-xl border-2 ${plan.borderClass} bg-gradient-to-br from-white via-navy-50 to-white p-5 shadow-inner`}
          data-testid={`agent-plan-freedom-${plan.id}`}
        >
          <p className="text-sm font-extrabold uppercase tracking-wide text-navy-800 mb-2 leading-tight">
            {plan.freedomTitle}
          </p>
          <p className="text-sm text-navy-700 leading-relaxed">
            {plan.freedomBody}
          </p>
        </div>

        {/* Policies */}
        <div className="space-y-3 mb-6" data-testid={`agent-plan-policies-${plan.id}`}>
          <FamilyNotice familyLabel={plan.familyLabel} />
          <IndividualNotice />
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-8 flex-1">
          {plan.features.map((feat, i) => (
            <li key={i} className="flex items-start gap-3">
              <div
                className={`mt-0.5 w-5 h-5 rounded-full bg-white border-2 ${plan.borderClass} ${plan.priceColor} flex items-center justify-center flex-shrink-0`}
              >
                <Check className="w-3 h-3" strokeWidth={3} />
              </div>
              <span className="text-navy-700 text-sm leading-relaxed">
                {feat}
              </span>
            </li>
          ))}
        </ul>

        {/* WhatsApp CTA only */}
        <a
          href={buildWhatsAppUrl(plan.name)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`agent-plan-cta-${plan.id}`}
          className="block"
        >
          <Button
            className={`w-full bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white font-bold py-6 text-base shadow-lg`}
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Coordinar por WhatsApp
          </Button>
        </a>
      </div>
    </motion.div>
  );
};

const AgentPricingPage = () => {
  return (
    <div className="min-h-screen bg-navy-50 font-body py-12 px-6" data-testid="agent-pricing-page">
      {/* Top nav */}
      <div className="max-w-7xl mx-auto mb-10 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-navy-600 hover:text-navy-900 transition-colors text-sm font-medium"
          data-testid="agent-pricing-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>
        <span
          className="text-xs uppercase tracking-widest text-navy-400 font-semibold"
          data-testid="agent-pricing-internal-label"
        >
          Precios · Agentes
        </span>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto text-center mb-14"
      >
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-navy-900 mb-4">
          Precios para Agentes
        </h1>
        <p className="text-navy-600 text-lg">
          Coordinamos cada venta por WhatsApp para hacer seguimiento personalizado.
        </p>
        <p className="text-navy-500 text-sm mt-3">
          Regla general: <strong>si es soltero o una persona sola, siempre paga $24.99 USD</strong>, en cualquiera de los planes.
        </p>
        <div className="mt-6">
          <Link to="/agentes/asesoramiento" data-testid="link-to-referral-form">
            <Button
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90 text-white font-bold px-8 py-6 shadow-lg"
            >
              <Send className="w-5 h-5 mr-2" />
              Enviar información de un cliente
            </Button>
          </Link>
          <p className="text-xs text-navy-500 mt-2">
            ¿Ya tienes un cliente listo? Envía sus datos y comenzamos el asesoramiento.
          </p>
        </div>
      </motion.div>

      {/* Plan cards */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>

      {/* Footer disclaimer */}
      <div className="max-w-3xl mx-auto mt-14 text-center text-navy-500 text-xs">
        <p>
          Página privada · Todas las ventas se coordinan directamente vía WhatsApp con un asesor.
        </p>
      </div>
    </div>
  );
};

export default AgentPricingPage;
